import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Box, Card, Chip, Container, Stack, Typography } from '@mui/material';
import { MapContainer, Marker, TileLayer, Popup, Polyline, CircleMarker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { getCleanerOverview } from '../helper/backend_helper';
import InfoTooltip from '../components/InfoTooltip';

const COMPANY_COLORS = ['#2563EB', '#16A34A', '#D97706', '#9333EA', '#0891B2', '#BE123C', '#4F46E5', '#0F766E'];
const DEFAULT_CENTER = [40.6401, 17.9416];
const SPIDER_AUTO_EXPAND_LIMIT = 5;
const CLUSTER_PRECISION = 4;
const LIVE_REFRESH_INTERVAL_MS = 5000;
const NAVIGATION_LINE_MAX_AGE_MS = 2 * 60 * 60 * 1000;

const formatMinutesAgo = (value) => {
  if (!value) return 'Never';
  const seenAt = new Date(value);
  const diffMs = Date.now() - seenAt.getTime();
  if (!Number.isFinite(diffMs)) return 'Never';
  const minutes = Math.max(0, Math.floor(diffMs / 60000));
  if (minutes < 1) return 'Now';
  if (minutes === 1) return '1 minute ago';
  return `${minutes} minutes ago`;
};

const formatArrival = (job) => job.checkInTime || job.arrivalTime || '';

const isFreshNavigationTarget = (target) => {
  if (!target?.latitude || !target?.longitude) return false;
  if (!target.clickedAt) return false;
  return Date.now() - new Date(target.clickedAt).getTime() <= NAVIGATION_LINE_MAX_AGE_MS;
};

const colorForCleaner = (key) => {
  const source = String(key || 'unknown');
  const hash = source.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0);
  return COMPANY_COLORS[hash % COMPANY_COLORS.length];
};

const makeCleanerIcon = (color) =>
  L.divIcon({
    className: 'cleaner-overview-marker',
    html: `<div style="width:24px;height:24px;border-radius:50%;background:${color};border:3px solid #fff;box-shadow:0 2px 10px rgba(0,0,0,.35);"></div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });

const getClusterKey = (job) => `${Number(job.latitude).toFixed(CLUSTER_PRECISION)}:${Number(job.longitude).toFixed(CLUSTER_PRECISION)}`;

const buildJobClusters = (jobs) => {
  const groups = new Map();
  jobs.forEach((job) => {
    if (!job.latitude || !job.longitude) return;
    const key = getClusterKey(job);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(job);
  });

  return Array.from(groups.entries()).map(([key, clusterJobs]) => ({
    key,
    latitude: clusterJobs.reduce((sum, job) => sum + Number(job.latitude), 0) / clusterJobs.length,
    longitude: clusterJobs.reduce((sum, job) => sum + Number(job.longitude), 0) / clusterJobs.length,
    jobs: clusterJobs,
  }));
};

const getSpiderPosition = (cluster, index) => {
  if (cluster.jobs.length <= 1) return [cluster.latitude, cluster.longitude];
  const angle = (Math.PI * 2 * index) / cluster.jobs.length;
  const radius = 0.00022 + Math.min(cluster.jobs.length, 12) * 0.000025;
  return [cluster.latitude + Math.sin(angle) * radius, cluster.longitude + Math.cos(angle) * radius];
};

const CleanerOverview = () => {
  const [overview, setOverview] = useState({ locations: [], jobs: [] });
  const [loading, setLoading] = useState(true);
  const [expandedClusters, setExpandedClusters] = useState({});

  useEffect(() => {
    let mounted = true;
    const loadOverview = async () => {
      try {
        const response = await getCleanerOverview();
        if (mounted) setOverview(response?.data?.data || { locations: [], jobs: [] });
      } catch (error) {
        console.error('Error loading cleaner overview:', error);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    loadOverview();
    // Keep the admin map close to live for cleaner GPS movement and completed-cleaning removals.
    const timer = setInterval(loadOverview, LIVE_REFRESH_INTERVAL_MS);
    return () => {
      mounted = false;
      clearInterval(timer);
    };
  }, []);

  const locations = overview.locations || [];
  const jobs = overview.jobs || [];
  const jobClusters = useMemo(() => buildJobClusters(jobs), [jobs]);
  const activeNavigationLines = useMemo(
    () =>
      locations
        .filter((item) => isFreshNavigationTarget(item?.navigatingTo))
        .map((item) => ({
          key: `${item.cleaner}-${item.deviceId}`,
          color: colorForCleaner(item.cleaner || item.cleanerName),
          positions: [
            [item.latitude, item.longitude],
            [item.navigatingTo.latitude, item.navigatingTo.longitude],
          ],
        })),
    [locations]
  );

  return (
    <>
      <Helmet>
        <title> Cleaner Overview </title>
      </Helmet>
      <Container maxWidth="xl">
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2} sx={{ mb: 3 }}>
          <Box>
            <Typography variant="h4">Cleaner Overview{' '}<InfoTooltip width={20} title="A live map of where your cleaners are and which BnBs need cleaning today. Cleaner positions come from the cleaner app's GPS and refresh automatically every few seconds, while job markers show today's check-out properties and any active navigation routes. Click a marker to expand clustered jobs and see arrival times, the assigned cleaner, and where each cleaner is heading." /></Typography>
            <Typography variant="body2" color="text.secondary">
              Live cleaner positions, today&apos;s arrivals, and active navigation targets.
            </Typography>
          </Box>
          <Stack direction="row" gap={1} flexWrap="wrap">
            <Chip label={`${locations.length} cleaner devices`} color="primary" variant="outlined" />
            <Chip label={`${jobs.length} jobs today`} color="warning" variant="outlined" />
            <Chip label={`${activeNavigationLines.length} navigating`} color="success" variant="outlined" />
          </Stack>
        </Stack>

        <Card sx={{ overflow: 'hidden', borderRadius: 1 }}>
          <Box sx={{ height: { xs: 520, md: 'calc(100vh - 210px)' }, minHeight: 520 }}>
            <MapContainer center={DEFAULT_CENTER} zoom={9} style={{ height: '100%', width: '100%' }}>
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />

              {jobClusters.map((cluster) => {
                const isExpanded = cluster.jobs.length < SPIDER_AUTO_EXPAND_LIMIT || expandedClusters[cluster.key];
                if (cluster.jobs.length > 1 && !isExpanded) {
                  return (
                    <CircleMarker
                      key={cluster.key}
                      center={[cluster.latitude, cluster.longitude]}
                      radius={16}
                      pathOptions={{ color: '#111827', fillColor: '#111827', fillOpacity: 0.85 }}
                      eventHandlers={{ click: () => setExpandedClusters((state) => ({ ...state, [cluster.key]: true })) }}
                    >
                      <Popup>
                        <Typography variant="subtitle2">{cluster.jobs.length} cleanings here</Typography>
                        <Typography variant="body2">Click the marker to expand spider view.</Typography>
                      </Popup>
                    </CircleMarker>
                  );
                }

                return cluster.jobs.map((job, index) => {
                  const position = cluster.jobs.length > 1 ? getSpiderPosition(cluster, index) : [job.latitude, job.longitude];
                  return (
                    <Fragment key={job.id || `${job.bnb}-${index}`}>
                      {cluster.jobs.length > 1 && (
                        <Polyline positions={[[cluster.latitude, cluster.longitude], position]} pathOptions={{ color: job.color || '#FF6C6C', weight: 2, opacity: 0.55 }} />
                      )}
                      <CircleMarker
                        center={position}
                        radius={9}
                        pathOptions={{ color: job.color || '#FF6C6C', fillColor: job.color || '#FF6C6C', fillOpacity: 0.85 }}
                      >
                        <Popup>
                          <Typography variant="subtitle2">{job.bnb}</Typography>
                          {formatArrival(job) && <Typography variant="body2">Arrival: {formatArrival(job)}</Typography>}
                          {job.cleanerName && <Typography variant="body2">Cleaner: {job.cleanerName}</Typography>}
                          {job.checkOutTime && <Typography variant="body2">Departure: {job.checkOutTime}</Typography>}
                        </Popup>
                      </CircleMarker>
                    </Fragment>
                  );
                });
              })}

              {activeNavigationLines.map((line) => (
                <Polyline key={line.key} positions={line.positions} pathOptions={{ color: line.color, weight: 4, opacity: 0.7 }} />
              ))}

              {locations.map((location) => {
                const color = colorForCleaner(location.cleaner || location.cleanerName);
                return (
                  <Marker
                    key={`${location.cleaner}-${location.deviceId}`}
                    position={[location.latitude, location.longitude]}
                    icon={makeCleanerIcon(color)}
                  >
                    <Popup>
                      <Typography variant="subtitle2">{location.cleanerName || 'Cleaner'}</Typography>
                      <Typography variant="body2">Last GPS: {formatMinutesAgo(location.lastSeenAt)}</Typography>
                      {isFreshNavigationTarget(location.navigatingTo) && location.navigatingTo?.bnb && (
                        <Typography variant="body2">Navigating to: {location.navigatingTo.bnb}</Typography>
                      )}
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>
          </Box>
        </Card>
        {loading && <Typography sx={{ mt: 2 }}>Loading overview...</Typography>}
      </Container>
    </>
  );
};

export default CleanerOverview;
